/**
 * Export utilities for handwriting generation results.
 * Supports PNG, ZIP, and PDF export formats.
 */

/**
 * Export a single canvas as a PNG blob.
 *
 * @param {HTMLCanvasElement} canvas - The canvas to export
 * @returns {Promise<Blob>} PNG image blob
 */
export async function exportPNG(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to create PNG blob'));
        }
      },
      'image/png',
      1.0
    );
  });
}

/**
 * Export multiple canvases as a ZIP file.
 * Requires JSZip to be loaded.
 *
 * @param {HTMLCanvasElement[]} canvases - Array of canvases (one per page)
 * @param {string} [filename='images.zip'] - Download filename
 * @returns {Promise<void>} Triggers browser download
 */
export async function exportZIP(canvases, filename = 'images.zip') {
  // Dynamic import of JSZip
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();

  for (let i = 0; i < canvases.length; i++) {
    const blob = await exportPNG(canvases[i]);
    const pageNum = String(i + 1).padStart(3, '0');
    zip.file(`${pageNum}.png`, blob);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  downloadBlob(zipBlob, filename);
}

/**
 * Export multiple canvases as a PDF file.
 * Requires jsPDF to be loaded.
 *
 * @param {HTMLCanvasElement[]} canvases - Array of canvases (one per page)
 * @param {object} [opts] - PDF options
 * @param {string} [opts.filename='images.pdf'] - Download filename
 * @param {string} [opts.pageSize='a4'] - PDF page size
 * @returns {Promise<void>} Triggers browser download
 */
export async function exportPDF(canvases, opts = {}) {
  const { filename = 'images.pdf' } = opts;

  // Dynamic import of jsPDF
  const { default: jsPDF } = await import('jspdf');

  // Create PDF with first page to determine dimensions
  const firstCanvas = canvases[0];
  const pdf = new jsPDF({
    orientation: firstCanvas.width > firstCanvas.height ? 'landscape' : 'portrait',
    unit: 'px',
  });

  for (let i = 0; i < canvases.length; i++) {
    const canvas = canvases[i];

    // Convert canvas to JPEG data URL for smaller PDF size
    const imgData = canvas.toDataURL('image/jpeg', 0.92);

    if (i > 0) {
      pdf.addPage();
    }

    // Fit canvas to PDF page
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // Scale to fit
    const scale = Math.min(
      pdfWidth / canvas.width,
      pdfHeight / canvas.height
    );
    const imgWidth = canvas.width * scale;
    const imgHeight = canvas.height * scale;
    const x = (pdfWidth - imgWidth) / 2;
    const y = (pdfHeight - imgHeight) / 2;

    pdf.addImage(imgData, 'JPEG', x, y, imgWidth, imgHeight);
  }

  pdf.save(filename);
}

/**
 * Download a single canvas as a PNG image file.
 *
 * @param {HTMLCanvasElement} canvas - The canvas
 * @param {string} [filename='handwrite.png'] - Download filename
 */
export async function downloadPNG(canvas, filename = 'handwrite.png') {
  const blob = await exportPNG(canvas);
  downloadBlob(blob, filename);
}

/**
 * Trigger a browser download for a blob.
 *
 * @param {Blob} blob - The blob to download
 * @param {string} filename - The download filename
 */
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Clean up the object URL after a short delay
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Get a data URL for a canvas (for preview display).
 *
 * @param {HTMLCanvasElement} canvas - The canvas
 * @param {string} [type='image/png'] - Image MIME type
 * @param {number} [quality=0.92] - Image quality (0-1)
 * @returns {string} Data URL
 */
export function canvasToDataURL(canvas, type = 'image/png', quality = 0.92) {
  return canvas.toDataURL(type, quality);
}
